// Dual-Space View hook
// Data fetching, selection state, filtering, highlighting

import { useState, useCallback, useEffect, useMemo } from 'react'
import { dualSpaceApi } from '@/lib/api/client'
import type { DualSpaceData, SpaceNode, CrossLink, ValuePropChain, Gap } from '@ispot/shared'

export function useDualSpace(accountId: string, projectId: string) {
  const [data, setData] = useState<DualSpaceData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Selection state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedSpace, setSelectedSpace] = useState<'problem' | 'solution' | null>(null)

  // Filter state
  const [personaFilter, setPersonaFilter] = useState<string | null>(null)
  const [jtbdFilter, setJtbdFilter] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await dualSpaceApi.getData(accountId, projectId, {
        personaSessionId: personaFilter || undefined,
        jtbdId: jtbdFilter || undefined,
      }) as DualSpaceData
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dual-space data')
    } finally {
      setIsLoading(false)
    }
  }, [accountId, projectId, personaFilter, jtbdFilter])

  useEffect(() => { load() }, [load])

  // Find selected node in either tree
  const selectedNode = useMemo(() => {
    if (!data || !selectedNodeId) return null
    return findNodeById(data.problemSpace, selectedNodeId)
      || findNodeById(data.solutionSpace, selectedNodeId)
  }, [data, selectedNodeId])

  // IDs of nodes cross-linked to the selected node
  const highlightedIds = useMemo(() => {
    if (!selectedNodeId || !data) return new Set<string>()
    return new Set(
      data.crossLinks
        .filter(l => l.sourceId === selectedNodeId || l.targetId === selectedNodeId)
        .map(l => l.sourceId === selectedNodeId ? l.targetId : l.sourceId),
    )
  }, [selectedNodeId, data])

  // All personas for the filter dropdown
  const personas = useMemo(() => {
    if (!data) return []
    return data.problemSpace.map(p => ({ id: p.id, title: p.title }))
  }, [data])

  // JTBDs for the selected persona
  const jtbds = useMemo(() => {
    if (!data || !personaFilter) return []
    const persona = data.problemSpace.find(p => p.id === personaFilter)
    if (!persona) return []
    return persona.children
      .filter(c => c.type === 'jtbd')
      .map(j => ({ id: j.id, title: j.title }))
  }, [data, personaFilter])

  const selectNode = useCallback((nodeId: string | null, space?: 'problem' | 'solution') => {
    setSelectedNodeId(prev => prev === nodeId ? null : nodeId)
    setSelectedSpace(space || null)
  }, [])

  const clearFilters = useCallback(() => {
    setPersonaFilter(null)
    setJtbdFilter(null)
  }, [])

  return {
    data, isLoading, error,
    selectedNode, selectedNodeId, selectedSpace, highlightedIds,
    selectNode, personas, jtbds,
    personaFilter, jtbdFilter, setPersonaFilter, setJtbdFilter, clearFilters,
    reload: load,
  }
}

function findNodeById(nodes: SpaceNode[], id: string): SpaceNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findNodeById(node.children, id)
    if (found) return found
  }
  return null
}
